// LapseModal (Story 10.5, UX-DR16 — AC 3, 6): the day-zero blocking interstitial.
//
// Presentation only — server-side write enforcement is Story 10.6; this modal
// sits over the read-only state so a clinician cannot be surprised mid-visit.
// Non-dismissible on purpose (AC3): the Dialog is permanently `open`, has no
// close affordance (`showClose` stays off), and Escape / outside interaction are
// prevented. The ONLY exits are the three contract actions; "View records
// (read-only)" acknowledges for THIS session (`sessionStorage`), so the modal
// returns next session while the entitlement is still expired.
//
// No client mockup exists — copy follows the story's field contract (UAT-flagged).

import { useRef } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/auth-context";
import { useRenewNow } from "@/hooks/queries/useRenewNow";

/**
 * Session-scoped acknowledgment flag (AC3b) — read by the LapseNotices host.
 * User-scoped so one clinician's ack never suppresses the next sign-in's
 * mandatory day-zero interstitial on a shared clinical workstation.
 */
export function lapseModalAckKey(userId: string): string {
  return `franklin:lapse-modal-ack:${userId}`;
}

export function readLapseModalAck(userId: string): boolean {
  try {
    return sessionStorage.getItem(lapseModalAckKey(userId)) === "true";
  } catch {
    return false; // private mode / storage disabled — keep showing the modal.
  }
}

export interface LapseModalProps {
  /** Scopes the session ack flag (shared-workstation safety). */
  userId: string;
  /** The passed entitlement horizon (never null in the expired phase). */
  validUntil: Date;
  /** Fired after "View records (read-only)" persists the session ack. */
  onAcknowledge: () => void;
}

export function LapseModal({ userId, validUntil, onAcknowledge }: LapseModalProps) {
  const { signOut } = useAuth();
  const { renewNow, isPending, isError } = useRenewNow();
  const renewButtonRef = useRef<HTMLButtonElement>(null);

  const endedOn = validUntil.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  const acknowledge = () => {
    try {
      sessionStorage.setItem(lapseModalAckKey(userId), "true");
    } catch {
      /* private mode — ack is best-effort; the modal simply reappears */
    }
    onAcknowledge();
  };

  return (
    <Dialog open>
      <DialogContent
        // This dialog opens SPONTANEOUSLY (the 30s poll / 60s tick flips the
        // phase mid-work, including mid-scoring). Radix's default initial focus
        // is the first tabbable — "Sign out", the most destructive action — so a
        // clinician's reflexive Enter could end the session. Land on the
        // contract's primary instead.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          renewButtonRef.current?.focus();
        }}
        onEscapeKeyDown={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Your subscription has expired.</DialogTitle>
          <DialogDescription>
            Your access ended on {endedOn}. Existing records remain available
            read-only. Renew to continue scoring.
          </DialogDescription>
        </DialogHeader>
        {isError ? (
          <p className="text-sm text-[color:var(--text-error)]" role="alert">
            Couldn't open the renewal page. Please try again.
          </p>
        ) : null}
        <DialogFooter>
          <Button variant="ghost" onClick={() => void signOut()}>
            Sign out
          </Button>
          <Button variant="secondary" onClick={acknowledge}>
            View records (read-only)
          </Button>
          <Button
            ref={renewButtonRef}
            variant="primary"
            onClick={renewNow}
            disabled={isPending}
          >
            Renew now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
